import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminPartners, formatError } from "../api";
import { Button, Card, PageHeader, Spinner, Switch } from "../ui";

export default function PartnersList() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  const load = () => adminPartners.list().then(setItems).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  if (!items) return <Spinner />;

  const togglePublished = async (p) => {
    try {
      await adminPartners.update(p.id, { ...p, published: !p.published });
      toast.success(p.published ? "Unpublished" : "Published");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete partner "${p.name}"?`)) return;
    try {
      await adminPartners.remove(p.id);
      toast.success("Partner deleted.");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="admin-partners-list">
      <PageHeader
        title="Partners & Stakeholders"
        description="Manage the partner cards that appear on the homepage strip."
        actions={
          <Link to="/admin/partners/new" data-testid="new-partner-btn">
            <Button><Plus className="h-3.5 w-3.5" /> New Partner</Button>
          </Link>
        }
      />
      {items.length === 0 ? (
        <Card className="p-10 text-center text-sm text-slate-500">
          No partners yet. Create your first one to see it on the homepage.
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.12em] text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Name</th>
                <th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-left">Order</th>
                <th className="px-4 py-3 text-left">Published</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`partner-row-${p.id}`}>
                  <td className="px-4 py-3 font-semibold text-[hsl(var(--aoa-navy))]">{p.name}</td>
                  <td className="px-4 py-3 text-slate-600">{p.category || "—"}</td>
                  <td className="px-4 py-3 text-slate-600 tabular-nums">{p.display_order}</td>
                  <td className="px-4 py-3"><Switch checked={!!p.published} onChange={() => togglePublished(p)} testId={`partner-pub-${p.id}`} /></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => nav(`/admin/partners/${p.id}`)} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-[hsl(var(--aoa-royal))] hover:underline mr-3"><Pencil className="h-3.5 w-3.5" /> Edit</button>
                    <button onClick={() => remove(p)} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.12em] text-red-600 hover:underline"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
